import Box from '@mui/material/Box';
import DownloadIcon from '@mui/icons-material/GetApp';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';
import React, {Fragment, useState} from 'react';
import ShareIcon from '@mui/icons-material/Share';
import Tooltip from '@mui/material/Tooltip';
import {useAuth} from '@apps-common/auth-react';
import {useDrag} from 'react-dnd';
import {useTranslation} from 'react-i18next';

import CopyButton from '../ui/copy-button.jsx';
import ItemType from '@explorer/components/ui/item-type';
import MenuButton from '@explorer/components/ui/nav-menu-button';
import MetadataModal from '../sidebars/search/results-panel/metadata-modal/index.jsx';
import ThumbnailCard from '@explorer/components/ui/thumbnail-card';
import ThumbnailImage from '@explorer/components/ui/thumbnail-image';
import {COMPARE_TYPES} from '@explorer/constants';
import {
  CloudCoverIndicator,
  IndicatorsContainer,
  PartialRectIndicator,
} from '@explorer/components/ui/metadata-indicators';
import {
  GROUP_ITEM_DIMENSIONS,
  ItemId,
  ListItem,
  RequestAccessButton,
  ItemTitle as Title,
} from './item-components.jsx';
import {getUrl as getThumbUrl} from '@explorer/util/thumbs';
import {trackEvent} from '@explorer/analytics.js';

const SceneItem = ({
  item,
  available,
  onRequestAccess,
  selected,
  onClick,
  onFocus,
  isComparing,
  showDownloadMenu,
  showSharingMenu,
  ...props
}) => {
  const auth = useAuth();

  const {t} = useTranslation();
  const [showInfo, setShowInfo] = useState(false);

  const [{isDragging}, dragRef] = useDrag(
    () => ({
      type: COMPARE_TYPES.SCENE,
      item: {
        resultId: props.resultId,
        ...item,
        properties: {
          ...item.properties,
          items: [item],
        },
      },
      canDrag: isComparing,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [isComparing]
  );

  const handleShowInfo = () => setShowInfo(true);
  const handleCloseInfo = () => setShowInfo(false);

  const partiallyRectified =
    ('ground_control' in item.properties &&
      item.properties.ground_control === false) ||
    ('ground_control_ratio' in item.properties &&
      item.properties.ground_control_ratio < 1.0);

  const cloudCover =
    item.properties.cloud_cover >= 0
      ? Math.round(item.properties.cloud_cover * 100)
      : null;

  const handleDownload = () => {
    trackEvent('Downloaded COG', {
      itemId: item.id,
      itemType: item.properties.item_type,
    });
    window.open(item._links.download);
  };

  const SecondaryAction = (
    <React.Fragment>
      {(showSharingMenu || showDownloadMenu) && (
        <MenuButton
          label={<MoreVertIcon fontSize="small" />}
          buttonComponent={IconButton}
          edge="end"
          title="More"
          data-qe="image-more-actions"
        >
          <MenuItem
            title="View Details"
            data-qe="image-item-link"
            onClick={handleShowInfo}
          >
            <ListItemIcon>
              <InfoOutlinedIcon />
            </ListItemIcon>
            View Details
          </MenuItem>
          {showSharingMenu && (
            <MenuItem
              title="Copy Link"
              data-qe="image-copy-link"
              onClick={props.onShareClick}
            >
              <ListItemIcon>
                <ShareIcon />
              </ListItemIcon>
              Copy Link
            </MenuItem>
          )}
          {showDownloadMenu && (
            <MenuItem
              title="Download"
              data-qe="image-download-link"
              onClick={handleDownload}
            >
              <ListItemIcon>
                <DownloadIcon />
              </ListItemIcon>
              Download
            </MenuItem>
          )}
        </MenuButton>
      )}
      {!(showSharingMenu || showDownloadMenu) && (
        <Tooltip title={t('itemsPanel.tooltipViewDetails')}>
          <IconButton
            edge="end"
            data-qe="image-item-link"
            onClick={handleShowInfo}
            size="large"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </React.Fragment>
  );

  const handleSceneIDCopy = (__, e) => {
    e.stopPropagation();
    trackEvent('Scene Item: Copied scene id', {
      sceneId: item.id,
      hostedData: Boolean(item.properties.hosted_metadata),
    });
  };

  return (
    <Fragment>
      <ListItem
        divider
        ref={dragRef}
        isDragging={isDragging}
        isComparing={isComparing}
        selected={selected}
        onClick={() => {
          !isComparing && onClick();
        }}
        onMouseEnter={() => onFocus(item)}
        onMouseLeave={() => onFocus(null)}
        button={!isComparing}
        data-qe={`image-list-item${selected ? '-selected' : ''}-${props.satId}`}
      >
        <ThumbnailCard
          height={GROUP_ITEM_DIMENSIONS}
          width={GROUP_ITEM_DIMENSIONS}
          marginRight={2}
          selected={selected}
          thumbnail={
            <ThumbnailImage
              src={getThumbUrl({item, token: auth.user?.accessToken})}
            />
          }
        >
          {!available && (
            <RequestAccessButton
              data-qe="scene-item-access-button"
              onClick={onRequestAccess}
            >
              {t('common:purchaseAccess', 'Purchase Access')}
            </RequestAccessButton>
          )}
        </ThumbnailCard>

        <ListItemText>
          <Title variant="subtitle2" gutterBottom data-qe="time-acquired">
            {t('common:time.long', {
              date: item.properties.acquired,
            })}
          </Title>
          <Box mb={1}>
            <ItemType
              itemType={item.properties.item_type}
              showIcon={false}
              typographyProps={{variant: 'caption', display: 'inline'}}
              data-qe="item-type"
            />
            <ItemId component="div" noWrap data-qe="scene-id">
              {item.properties.item_id || item.id}
            </ItemId>
            <CopyButton
              onCopy={handleSceneIDCopy}
              textToCopy={item.properties.item_id || item.id}
              title="Scene ID"
            />
          </Box>
          <IndicatorsContainer>
            {cloudCover !== null && (
              <CloudCoverIndicator
                cloudPercent={cloudCover}
                data-qe="item-cloud-cover"
              />
            )}
            {partiallyRectified && (
              <PartialRectIndicator
                data-qe="item-group-item-partial-rectification"
                tooltip="Partially rectified"
              />
            )}
          </IndicatorsContainer>
        </ListItemText>
        <ListItemSecondaryAction>{SecondaryAction}</ListItemSecondaryAction>
      </ListItem>
      {showInfo && (
        <MetadataModal open={showInfo} onClose={handleCloseInfo} item={item} />
      )}
    </Fragment>
  );
};

SceneItem.propTypes = {
  available: PropTypes.bool,
  isComparing: PropTypes.bool,
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onRequestAccess: PropTypes.func,
  onShareClick: PropTypes.func,
  resultId: PropTypes.string,
  satId: PropTypes.string,
  selected: PropTypes.bool,
  showDownloadMenu: PropTypes.bool,
  showSharingMenu: PropTypes.bool,
};

export default SceneItem;
